import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './AccessPage.css';

const AccessPage = () => {
    const [credentials, setCredentials] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [generatedPassword, setGeneratedPassword] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [passwordLength, setPasswordLength] = useState(12);

    useEffect(() => {
        loadCredentials();
    }, []);

    const loadCredentials = async () => {
        try {
            setLoading(true);
            const response = await api.get('/app-access/credentials');
            setCredentials(response.data);
        } catch (error) {
            console.error('Ошибка загрузки учетных данных:', error);
            if (error.response?.status !== 404) {
                alert('Ошибка загрузки данных');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleGeneratePassword = async () => {
        if (!window.confirm(
            credentials 
                ? 'Вы уверены? Старый пароль будет заменен новым. После закрытия окна пароль будет недоступен для просмотра.'
                : 'Сгенерировать пароль для входа в iOS приложение?'
        )) {
            return;
        }

        try {
            setGenerating(true);
            const response = await api.post('/app-access/generate-password', {
                password_length: passwordLength
            });
            
            setGeneratedPassword(response.data);
            setShowPassword(true);
            
            // Перезагружаем credentials
            await loadCredentials();
        } catch (error) {
            console.error('Ошибка генерации пароля:', error);
            alert(error.response?.data?.detail || 'Ошибка генерации пароля');
        } finally {
            setGenerating(false);
        }
    };

    const handleRevokeAccess = async () => {
        if (!window.confirm('Вы уверены, что хотите отозвать доступ к приложению? Вы не сможете войти с текущими учетными данными.')) {
            return;
        }

        try {
            await api.delete('/app-access/revoke');
            alert('Доступ к приложению успешно отозван');
            await loadCredentials();
        } catch (error) {
            console.error('Ошибка отзыва доступа:', error);
            alert(error.response?.data?.detail || 'Ошибка отзыва доступа');
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        alert('Скопировано в буфер обмена');
    };

    const closePasswordModal = () => {
        setGeneratedPassword(null);
        setShowPassword(false);
    };

    if (loading) {
        return (
            <div className="access-page">
                <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
                    Загрузка...
                </div>
            </div>
        );
    }

    return (
        <div className="access-page">
            <div className="access-header">
                <h1>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ verticalAlign: 'middle', marginRight: '8px' }}>
                        <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                        <line x1="12" y1="18" x2="12.01" y2="18"/>
                    </svg>
                    Доступ к iOS приложению
                </h1>
                <p className="access-subtitle">
                    Управление паролем для входа в мобильное приложение MarkApp
                </p>
            </div>

            <div className="access-content">
                {/* Текущие учетные данные */}
                <div className="access-card">
                    <h2>Учетные данные</h2>
                    
                    {credentials ? (
                        <div className="credentials-info">
                            <div className="info-row">
                                <span className="info-label">Номер телефона:</span>
                                <span className="info-value">{credentials.phone}</span>
                            </div>
                            
                            <div className="info-row">
                                <span className="info-label">Статус:</span>
                                <span className={`status-badge ${credentials.is_active ? 'active' : 'inactive'}`}>
                                    {credentials.is_active ? 'Активен' : 'Отозван'}
                                </span>
                            </div>
                            
                            <div className="info-row">
                                <span className="info-label">Создан:</span>
                                <span className="info-value">
                                    {new Date(credentials.created_at).toLocaleString('ru-RU')}
                                </span>
                            </div>
                            
                            {credentials.last_login_at && (
                                <div className="info-row">
                                    <span className="info-label">Последний вход:</span>
                                    <span className="info-value">
                                        {new Date(credentials.last_login_at).toLocaleString('ru-RU')}
                                    </span>
                                </div>
                            )}
                            
                            <div className="password-note">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"/>
                                    <path d="M12 16v-4M12 8h.01"/>
                                </svg>
                                Пароль не отображается по соображениям безопасности
                            </div>
                        </div>
                    ) : (
                        <div className="no-credentials">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                            </svg>
                            <p>Учетные данные еще не созданы</p>
                            <p className="hint">Сгенерируйте пароль для входа в iOS приложение</p>
                        </div>
                    )}
                </div>

                {/* Генерация пароля */}
                <div className="access-card">
                    <h2>Генерация пароля</h2>
                    
                    <div className="password-generator">
                        <div className="form-group">
                            <label htmlFor="passwordLength">Длина пароля:</label>
                            <div className="length-input-group">
                                <input
                                    type="range"
                                    id="passwordLength"
                                    min="8"
                                    max="32"
                                    value={passwordLength}
                                    onChange={(e) => setPasswordLength(Number(e.target.value))}
                                    className="password-slider"
                                />
                                <span className="length-display">{passwordLength} символов</span>
                            </div>
                        </div>
                        
                        <button
                            className="btn btn-primary"
                            onClick={handleGeneratePassword}
                            disabled={generating}
                        >
                            {generating ? (
                                'Генерация...'
                            ) : credentials ? (
                                <>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ verticalAlign: 'middle', marginRight: '6px' }}>
                                        <polyline points="23 4 23 10 17 10"/>
                                        <polyline points="1 20 1 14 7 14"/>
                                        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                                    </svg>
                                    Обновить пароль
                                </>
                            ) : (
                                <>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ verticalAlign: 'middle', marginRight: '6px' }}>
                                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                                    </svg>
                                    Создать пароль
                                </>
                            )}
                        </button>
                        
                        {credentials && credentials.is_active && (
                            <button
                                className="btn btn-danger"
                                onClick={handleRevokeAccess}
                                style={{ marginLeft: '12px' }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ verticalAlign: 'middle', marginRight: '6px' }}>
                                    <circle cx="12" cy="12" r="10"/>
                                    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                                </svg>
                                Отозвать доступ
                            </button>
                        )}
                    </div>
                    
                    <div className="security-tips">
                        <h3>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ verticalAlign: 'middle', marginRight: '6px' }}>
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="12" y1="16" x2="12" y2="12"/>
                                <line x1="12" y1="8" x2="12.01" y2="8"/>
                            </svg>
                            Советы по безопасности:
                        </h3>
                        <ul>
                            <li>Сохраните пароль в надежном месте сразу после генерации</li>
                            <li>Не передавайте пароль третьим лицам</li>
                            <li>Регулярно обновляйте пароль (раз в 3-6 месяцев)</li>
                            <li>Используйте уникальный пароль для каждого сервиса</li>
                        </ul>
                    </div>
                </div>

                {/* Инструкция по входу */}
                <div className="access-card">
                    <h2>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ verticalAlign: 'middle', marginRight: '6px' }}>
                            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                        </svg>
                        Как войти в iOS приложение
                    </h2>
                    
                    <ol className="instructions-list">
                        <li>
                            <strong>Скачайте приложение MarkApp</strong>
                            <p>Откройте App Store на вашем iPhone</p>
                        </li>
                        <li>
                            <strong>Откройте экран входа</strong>
                            <p>Нажмите "Вход по номеру телефона"</p>
                        </li>
                        <li>
                            <strong>Введите учетные данные</strong>
                            <p>Укажите ваш номер телефона и пароль из этого раздела</p>
                        </li>
                        <li>
                            <strong>Готово!</strong>
                            <p>Вы вошли в приложение MarkApp</p>
                        </li>
                    </ol>
                </div>
            </div>

            {/* Модальное окно с паролем */}
            {generatedPassword && showPassword && (
                <div className="modal-overlay" onClick={closePasswordModal}>
                    <div className="password-modal" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={closePasswordModal}>×</button>
                        
                        <div className="modal-header">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                                <path d="M9 12l2 2 4-4"/>
                            </svg>
                            <h2>{generatedPassword.message}</h2>
                        </div>
                        
                        <div className="modal-body">
                            <div className="generated-credentials">
                                <div className="cred-item">
                                    <label>Номер телефона:</label>
                                    <div className="cred-value">
                                        <code>{generatedPassword.phone}</code>
                                        <button
                                            className="copy-btn"
                                            onClick={() => copyToClipboard(generatedPassword.phone)}
                                            title="Скопировать"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                                
                                <div className="cred-item">
                                    <label>Пароль:</label>
                                    <div className="cred-value password-value">
                                        <code>{generatedPassword.password}</code>
                                        <button
                                            className="copy-btn"
                                            onClick={() => copyToClipboard(generatedPassword.password)}
                                            title="Скопировать"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="modal-warning">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                                    <line x1="12" y1="9" x2="12" y2="13"/>
                                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                                </svg>
                                <p>
                                    <strong>Важно!</strong> Сохраните пароль прямо сейчас. 
                                    После закрытия этого окна вы не сможете увидеть его снова.
                                </p>
                            </div>
                        </div>
                        
                        <div className="modal-footer">
                            <button className="btn btn-primary" onClick={closePasswordModal}>
                                Я сохранил пароль
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccessPage;

